import { ArrowBack, Close } from "@mui/icons-material";
import { AppBar, Box, CircularProgress, IconButton, List, ListItem, ListItemText, Switch, Toolbar, Typography } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { SystemSwitch } from "../../../components/UIToolkit/Clickable/SystemSwitch";
import { useAuth } from "../../../contexts/AuthProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { trackErrorEvent, trackEvent } from "../../../utils/analytics";
import { NOTIFICATION_TYPE_LABELS, NotificationField, NotificationSettingsType } from "../../../utils/models";
import axiosInstance from "../../../utils/networkWrapper";
import { toPascalCase } from "../../../utils/strings";

interface NotificationSetting {
	notification_type: NotificationSettingsType;
	email: boolean;
	push_notifications: boolean;
	sms: boolean;
	exclude_agency: boolean;
}

export const NotificationTypeSettings: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { chosenDepartment } = useHospitalData();
	const { userInfo } = useAuth();

	// Check if user has a push notification token
	const hasPushToken = !!userInfo?.push_notification_token;

	const { notificationType: kebabNotificationType } = useParams<{
		notificationType: string;
	}>();

	// Convert kebab-case URL param back to PascalCase enum value
	const notificationType = toPascalCase(kebabNotificationType || "") as NotificationSettingsType;

	const [settings, setSettings] = useState<NotificationSetting>({
		notification_type: notificationType as NotificationSettingsType,
		email: true,
		push_notifications: false,
		sms: false,
		exclude_agency: false,
	});
	const [hasChanges, setHasChanges] = useState(false);
	const [saving, setSaving] = useState(false);
	const { showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const [loading, setLoading] = useState(true);
	const handleToggle = (field: NotificationField) => {
		if (field === NotificationField.PushNotifications && !hasPushToken) {
			trackEvent("Push notifications is disabled for this user.", {
				userId: userInfo?.id,
				notificationType: notificationType,
				field: field,
				hasPushToken: hasPushToken,
			});
			return;
		}

		setSettings((prev) => ({
			...prev,
			[field]: !prev[field],
		}));
		setHasChanges(true);

		trackEvent("notification_type_setting_toggled", {
			userId: userInfo?.id,
			notificationType: notificationType,
			field: field,
			hasPushToken: hasPushToken,
			role: userInfo.role,
		});
	};

	const handleExcludeAgencyToggle = () => {
		setSettings((prev) => ({ ...prev, exclude_agency: !prev.exclude_agency }));
		setHasChanges(true);
		trackEvent("notification_exclude_agency_toggled", {
			userId: userInfo?.id,
			notificationType: notificationType,
			role: userInfo.role,
		});
	};

	useEffect(() => {
		const loadNotificationSettings = async () => {
			setLoading(true);
			if (!userInfo?.id || !notificationType) return;

			try {
				trackEvent("notification_type_settings_load_started", {
					user_id: userInfo?.id,
					notificationType: notificationType,
				});

				const response = await axiosInstance.get("/settings/notification_settings");

				// Find the specific notification type settings
				const userSettings = response.data[notificationType];
				if (userSettings) {
					trackEvent("notification_type_settings_loaded_success", {
						user_id: userInfo?.id,
						notificationType,
						settings: userSettings,
					});
					setSettings({
						...userSettings,
						// Force SMS to true for ShiftChanges notification type
						sms: notificationType === NotificationSettingsType.ShiftChanges ? true : userSettings.sms,
					});
				} else {
					const defaultSettings = {
						notification_type: notificationType as NotificationSettingsType,
						email: true,
						push_notifications: false,
						sms: notificationType === NotificationSettingsType.ShiftChanges,
						exclude_agency: false,
					};
					setSettings(defaultSettings);
					trackEvent("notification_type_settings_loaded_default", {
						user_id: userInfo?.id,
						notificationType,
						settings: defaultSettings,
					});
				}
			} catch (error) {
				trackErrorEvent("notification_type_settings_load_failed", {
					user_id: userInfo?.id,
					notificationType,
					error: error,
				});
				showErrorSnackbar(t("notification.settings.load.failed"), { userId: userInfo?.id }, error);
			} finally {
				setLoading(false);
			}
		};

		loadNotificationSettings();
	}, [notificationType, userInfo?.id, t, showErrorSnackbar]);

	function toSnakeCase(str: string) {
		return str
			.replace(/([A-Z])/g, " $1")
			.toLowerCase()
			.replace(/^_/, "");
	}

	const handleBack = async () => {
		if (hasChanges) {
			setSaving(true);
			try {
				await axiosInstance.post("/settings/notification_settings", settings);

				trackEvent("notification_type_settings_auto_saved_on_back", {
					user_id: userInfo?.id,
					notificationType,
					settings,
					role: userInfo.role,
				});

				const path = `/${chosenDepartment.department_id}/settings/notification-settings`;
				navigate(path);
				showSnackbar(`${t("notification")} ${toSnakeCase(settings.notification_type)} ${t("settings.saved.successfully")}`, "success");
			} catch (error) {
				trackErrorEvent("notification_type_settings_auto_save_failed", {
					user_id: userInfo?.id,
					notificationType,
					settings,
					error: error,
				});
				showErrorSnackbar(t("notification.settings.save.failed"), { userId: userInfo?.id }, error);
				setSaving(false);
				return;
			}
		} else {
			// No changes, just navigate back
			const path = `/${chosenDepartment.department_id}/settings/notification-settings`;
			navigate(path);
		}
	};

	const handleClose = () => {
		const path = `/${chosenDepartment.department_id}/settings/notification-settings`;
		navigate(path);
	};

	if (loading || !settings) {
		return <CircularProgress />;
	}

	// The "Exclude agency" toggle only applies to the Daily Pickup Reminder.
	const showExcludeAgency = notificationType === NotificationSettingsType.DailyPickupSummary;

	const settingsConfig = [
		{
			key: NotificationField.Email,
			label: t("email"),
			value: settings.email,
			disabled: false,
		},
		{
			key: NotificationField.PushNotifications,
			label: t("push.notifications"),
			value: settings.push_notifications,
			disabled: !hasPushToken,
		},
		{
			key: NotificationField.SMS,
			label: t("sms"),
			value: settings.sms,
			disabled: notificationType === NotificationSettingsType.ShiftChanges,
		},
	];

	const switchSx = {
		"& .MuiSwitch-switchBase.Mui-checked": {
			color: "#fff",
		},
		"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
			backgroundColor: "#1976d2",
		},
		// Style for disabled but checked state (greyed out but ON)
		"& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": {
			color: "#fff !important",
			opacity: 0.6,
		},
		"& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track": {
			backgroundColor: "#1976d2 !important",
			opacity: 0.4,
		},
		// Style for disabled and unchecked state
		"& .MuiSwitch-switchBase.Mui-disabled": {
			color: "#ccc",
		},
		"& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
			backgroundColor: "#f0f0f0",
		},
	} as const;

	// Shared card styling for each settings section.
	const cardSx = {
		mx: 2,
		backgroundColor: "#fff",
		borderRadius: 2,
		border: "1px solid #e9ecef",
		boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
	} as const;

	return (
		<Box
			sx={{
				height: "100vh",
				backgroundColor: "#fff",
				position: "relative",
				overflow: "hidden",
			}}
		>
			{/* AppBar Header */}
			<AppBar
				position="static"
				elevation={0}
				sx={{
					backgroundColor: "#fff",
					color: "#000",
					marginTop: "60px",
				}}
			>
				<Toolbar>
					{/* Left side: Back Arrow */}
					<IconButton edge="start" onClick={handleBack} disabled={saving} sx={{ p: 0.5 }}>
						<ArrowBack />
					</IconButton>

					{/* Center: Title */}
					<Typography
						variant="h6"
						sx={{
							flexGrow: 1,
							textAlign: "center",
							fontWeight: 600,
							color: "inherit",
						}}
					>
						{t(NOTIFICATION_TYPE_LABELS[notificationType])}
					</Typography>

					{/* Right side: Close Button (X) */}
					<IconButton
						edge="end"
						onClick={handleClose}
						sx={{
							p: 0.5,
							"& .MuiSvgIcon-root": {
								fontSize: "18px",
							},
						}}
					>
						<Close />
					</IconButton>
				</Toolbar>
			</AppBar>
			{/* Exclude agency: Daily Pickup Reminder only — plain row, no card frame, above the channel toggles. */}
			{showExcludeAgency && (
				<ListItem sx={{ py: 2.5, px: 5 }}>
					<ListItemText
						primary={t("notification.settings.exclude-agency")}
						secondary={t("notification.settings.exclude-agency.tooltip")}
						slotProps={{
							primary: { sx: { fontSize: "16px", fontWeight: 500 } },
							secondary: { sx: { fontSize: "14px", color: "#666" } },
						}}
					/>
					<SystemSwitch checked={settings.exclude_agency} onChange={handleExcludeAgencyToggle} />
				</ListItem>
			)}

			{/* Channel toggles (Email / Push / SMS) */}
			<Box sx={{ ...cardSx, mt: showExcludeAgency ? 1 : 0 }}>
				{/* Settings List */}
				<List sx={{ p: 0 }}>
					{settingsConfig.map((setting, index) => (
						<ListItem
							key={setting.key}
							sx={{
								py: 2.5,
								px: 3,
								borderBottom: index < settingsConfig.length - 1 ? "1px solid #f0f0f0" : "none",
								opacity: setting.disabled ? 0.5 : 1,
							}}
						>
							<ListItemText
								primary={setting.label}
								secondary={setting.disabled && setting.key === NotificationField.PushNotifications ? t("download.app.push.notifications") : undefined}
								slotProps={{
									primary: {
										sx: {
											fontSize: "16px",
											fontWeight: 500,
											color: setting.disabled ? "#999" : "inherit",
										},
									},

									secondary: {
										sx: {
											fontSize: "14px",
											color: "#999",
										},
									},
								}}
							/>
							<Switch checked={setting.value} onChange={() => handleToggle(setting.key)} disabled={setting.disabled} sx={switchSx} />
						</ListItem>
					))}
				</List>
			</Box>
		</Box>
	);
};
